import { AutoProductTaggerTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/AutoProductTaggerTemplateIcon";
import { ChatGPTCloneTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/ChatGPTCloneTemplateIcon";
import { CheckoutUpsellTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/CheckoutUpsellTemplateIcon";
import { CrudBlogTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/CrudBlogTemplateIcon";
import { CustomerWishlistTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/CustomerWishlistTemplateIcon";
import { CustomizedBundleTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/CustomizedBundleTemplateIcon";
import { MonthlySubscriptionTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/MonthlySubscriptionTemplateIcon";
import { NotionTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/NotionTemplateIcon";
import { OnetimeChargeTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/OnetimeChargeTemplateIcon";
import { ProductQuizTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/ProductQuizTemplateIcon";
import { ScreenwriterCloneTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/ScreenwriterCloneTemplateIcon";
import { SlackNotificationTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/SlackNotificationTemplateIcon";
import { TailwindCSSTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/TailwindCSSTemplateIcon";
import { UsageBillingTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/UsageBillingTemplateIcon";
import { CloudUserGroupIcon } from "@gadgetinc/widgets/src/icons/CloudUserGroupIcon";
import { GlobeIcon } from "@gadgetinc/widgets/src/icons/GlobeIcon";
import { ReactIcon } from "@gadgetinc/widgets/src/icons/ReactIcon";
import { ReactRouterIcon } from "@gadgetinc/widgets/src/icons/ReactRouterIcon";
import { RemixShopifyIcon } from "@gadgetinc/widgets/src/icons/RemixShopifyIcon";
import { SecuredCloudStorageIcon } from "@gadgetinc/widgets/src/icons/SecuredCloudStorageIcon";
import { useStyletron } from "baseui";
import { colors } from "baseui/tokens";
import React from "react";
import { APP_TEMPLATE_IDS, AppTemplateType, WebAppAuthType } from "state-trees/src/AppTemplateType";
import type { AutoComponentType, FrontendFramework } from "state-trees/src/frontend";
import type { GadgetFlags } from "state-trees/src/GadgetFlags";
import type { KeyPath } from "useful-types";
import type { AppTemplateTagType } from "../components/edit/editor/AppTemplates";
import { Row, StaffPreviewPill } from "@gadgetinc/widgets";

type DefaultProp = { default: boolean; applicationId?: never };
type WithoutDefaultProp = { default?: never; applicationId: string };

export type AppTemplateDescriptor = {
  title: string;
  description: string;
  icon: React.ReactNode;
  externalLink?: string;
  tags?: AppTemplateTagType[];
  frontendFramework?: FrontendFramework;
  withAutoComponents?: AutoComponentType;
  requiredFlags?: KeyPath<GadgetFlags>[];
  allowedLanguages: ("javascript" | "typescript")[];
  staffOnly?: boolean;
  hideInTemplatesPage?: boolean;
} & (DefaultProp | WithoutDefaultProp);

export const TemplateIcon = (props: { size?: string | number; backgroundColor?: string; children: React.ReactNode }) => {
  const [css, $theme] = useStyletron();

  return (
    <div
      className={css({
        backgroundColor: props.backgroundColor || colors.gray100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: typeof props.size === "number" ? `${props.size}px` : props.size || "20px",
        height: typeof props.size === "number" ? `${props.size}px` : props.size || "20px",
        borderRadius: $theme.borders.radius200,
      })}
    >
      {props.children}
    </div>
  );
};

export const SHOPIFY_GREEN = "#558B2F";

const ReactTemplateIcon = <ReactIcon color="#545454" />;

const ReactRouterTemplateIcon = <ReactRouterIcon color="#545454" />;

const allowInviteAuthApps = false;

export const WebAppAuthOptions = {
  [WebAppAuthType.EmailPassword]: {
    title: "Multi-party auth",
    subtitle: "SaaS apps",
    description: "For SaaS apps. Each user can only access their own data.",
    icon: <CloudUserGroupIcon />,
  },
  ...(allowInviteAuthApps
    ? {
        [WebAppAuthType.InviteAuth]: {
          title: "Single-party auth",
          subtitle: "Internal tools",
          description: "For personal or small business tools. Once invited, users can access all of the application's data.",
          icon: <SecuredCloudStorageIcon />,
        },
      }
    : {}),
  [WebAppAuthType.NoAuth]: {
    title: "No user auth",
    subtitle: "Open-access apps",
    description: "No signup required. All data is public and accessible to anyone on the internet.",
    icon: <GlobeIcon color="black" />,
  },
};

export const ChatGPTAuthOptions = {
  [WebAppAuthType.EmailPassword]: {
    ...WebAppAuthOptions[WebAppAuthType.EmailPassword],
    description: "Anyone can sign up. Each user has their own account and data. GPT OAuth requires login.",
  },
  [WebAppAuthType.NoAuth]: {
    ...WebAppAuthOptions[WebAppAuthType.NoAuth],
    description: "Public. No login, all data is shared. GPT OAuth does not require login.",
  },
};

export const webAppAuthTypeToAppTemplateType = (authType: WebAppAuthType) => {
  switch (authType) {
    case WebAppAuthType.EmailPassword:
      return AppTemplateType.WebAppWithAuth;
    case WebAppAuthType.InviteAuth:
      return AppTemplateType.InternalTool;
    case WebAppAuthType.NoAuth:
      return AppTemplateType.Empty;
  }
};

/**
 * This is a list of app templates that can be used to create new apps.
 */
const AppTemplates: {
  [key in AppTemplateType]: {
    [key: string]: AppTemplateDescriptor[];
  };
} = {
  [AppTemplateType.ShopifyApp]: {
    // This is a special key that is used to group templates that don't belong to any group.
    Base: [
      {
        default: true,
        frontendFramework: "react-router",
        title: "React Router v7 framework mode",
        description: "Basic Shopify embedded app powered by React Router v7 in framework mode",
        icon: ReactRouterTemplateIcon,
        tags: ["remix", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
      },
      {
        default: false,
        title: "React Router v7 declarative mode",
        frontendFramework: "react",
        description: "This template is a basic Shopify embedded app that can be extended to create any Shopify app.",
        icon: <ReactIcon color={SHOPIFY_GREEN} />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
      },
    ],

    "Embedded app": [
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_PRODUCT_TAGGER_TEMPLATE_ID,
        title: "Automated product tagger",
        description: "Tags products based on keywords in descriptions.",
        externalLink: "https://gadget.dev/resources/templates/product-tagger-template",
        icon: <AutoProductTaggerTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "remix",
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_SLACK_NOTIFICATIONS_TEMPLATE_ID,
        title: "Slack notifications",
        description: "Merchants receive notifications when a sale goes through.",
        externalLink: "https://gadget.dev/resources/templates/slack-notification-template",
        icon: <SlackNotificationTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_NOTION_INTEGRATION_TEMPLATE_ID,
        title: "Notion integration",
        description: "Merchants can integrate their Shopify store with Notion.",
        externalLink: "https://gadget.dev/resources/templates/shopify-notion-integration",
        icon: <NotionTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "remix",
      },
    ],
    Extensions: [
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_CUSTOMER_WISHLIST_TEMPLATE_ID,
        title: "Customer wishlist",
        description: "Allows customers to save desired products in wish-lists.",
        externalLink: "https://gadget.dev/resources/templates/wishlist-template",
        icon: <CustomerWishlistTemplateIcon />,
        tags: ["react", "theme-extension", "customer-extension"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "remix",
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_CUSTOMIZED_BUNDLE_TEMPLATE_ID,
        title: "Customized bundle",
        description: "Allow merchants to make customizable bundles for purchase.",
        externalLink: "https://gadget.dev/resources/templates/customized-bundles-template",
        icon: <CustomizedBundleTemplateIcon />,
        tags: ["react", "shopify-cart-transform-function"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "remix",
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_PREPURCHASE_UPSELL_TEMPLATE_ID,
        title: "Checkout upsell",
        description: "Offers pre-selected products to customers on checkout.",
        externalLink: "https://gadget.dev/resources/templates/pre-purchase-template",
        icon: <CheckoutUpsellTemplateIcon />,
        tags: ["react", "checkout-extension"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_PRODUCT_QUIZ_TEMPLATE_ID,
        title: "Product quiz",
        description: "Recommends products to customers based on answers to a quiz.",
        externalLink: "https://gadget.dev/resources/templates/product-quiz-template",
        icon: <ProductQuizTemplateIcon />,
        tags: ["react", "theme-extension"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "remix",
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_PRODUCT_REVIEWS_TEMPLATE_ID,
        title: "Product reviews",
        description: "Allow customers to leave reviews for products.",
        externalLink: "https://gadget.dev/resources/templates/product-reviews-template",
        icon: <SlackNotificationTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "remix",
      },
    ],
    Billing: [
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_MONTHLY_SUBSCRIPTION_TEMPLATE_ID,
        title: "Monthly subscription",
        description: "Charge merchants monthly for using your app.",
        externalLink: "https://gadget.dev/resources/templates/monthly-subscription-template",
        icon: <MonthlySubscriptionTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_ONE_TIME_CHARGE_TEMPLATE_ID,
        title: "One-time charge",
        description: "Charge merchants once for access to your app.",
        externalLink: "https://gadget.dev/resources/templates/one-time-charge-template",
        icon: <OnetimeChargeTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
      {
        applicationId: APP_TEMPLATE_IDS.SHOPIFY_USAGE_BASED_SUBSCRIPTION_TEMPLATE_ID,
        title: "Usage-based subscription",
        description: "Charge merchants for the amount that they use your app.",
        externalLink: "https://gadget.dev/resources/templates/usage-subscription-template",
        icon: <UsageBillingTemplateIcon />,
        tags: ["react", "embedded"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
    ],
  },
  [AppTemplateType.WebAppWithAuth]: {
    // This is a special key that is used to group templates that don't belong to any group.
    Base: [
      {
        title: "React Router v7 framework mode",
        default: true,
        description: "Gadget's built-in email/password auth and Google Oauth powered by React Router v7 in framework mode",
        icon: ReactRouterTemplateIcon,
        frontendFramework: "react-router",
        tags: ["react-router", "auth", "carleton-hackathon-2025"],
        allowedLanguages: ["typescript", "javascript"],
        withAutoComponents: "shadcn",
      },
      {
        default: false,
        title: "React Router v7 declarative mode",
        description: "This template has Gadget's built-in email/password auth and Google Oauth.",
        icon: ReactTemplateIcon,
        frontendFramework: "react",
        tags: ["react", "auth"],
        allowedLanguages: ["typescript", "javascript"],
        withAutoComponents: "shadcn",
      },
    ],
    Starters: [
      // Stripe template isn't ready yet.
      // {
      //   applicationId: "102336",
      //   title: "Stripe Billing",
      //   description: "This template has Stripe billing and auth.",
      //   externalLink: "https://gadget.dev/resources/templates/stripe-billing-template",
      //   icon: (
      //     <TemplateIcon backgroundColor={colors.gray100}>
      //       <StripeIcon $size={16} />
      //     </TemplateIcon>
      //   ),
      //   tags: ["auth"],
      // },
      {
        applicationId: APP_TEMPLATE_IDS.WEB_TAILWIND_TEMPLATE_ID,
        title: "Tailwind CSS",
        description: "TailwindCSS pre-installed and can be extended to create any interface.",
        externalLink: "https://gadget.dev/resources/templates/tailwind-css-template",
        icon: <TailwindCSSTemplateIcon />,
        tags: ["react", "auth"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
    ],
    "Sample apps": [
      {
        applicationId: APP_TEMPLATE_IDS.WEB_CRUD_BLOG_TEMPLATE_ID,
        title: "CRUD blog",
        description: "CRUD actions with public and protected routes.",
        externalLink: "https://gadget.dev/resources/templates/blog-template",
        icon: <CrudBlogTemplateIcon />,
        tags: ["react", "auth"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react-router",
      },
      {
        applicationId: APP_TEMPLATE_IDS.WEB_CHATGPT_CLONE_TEMPLATE_ID,
        title: "ChatGPT clone",
        description: "Built-in OpenAI connection to showcase how a chatbot can be created.",
        externalLink: "https://gadget.dev/resources/templates/chatgpt-template",
        icon: <ChatGPTCloneTemplateIcon />,
        tags: ["react", "openai", "auth"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react",
        hideInTemplatesPage: true,
      },
      {
        applicationId: APP_TEMPLATE_IDS.WEB_SCREENWRITER_GPT_CLONE_TEMPLATE_ID,
        title: "Screenwriter GPT clone",
        description: "Built-in OpenAI connection to create and stream a unique movie script.",
        externalLink: "https://gadget.dev/resources/templates/ai-screenwriter-template",
        icon: <ScreenwriterCloneTemplateIcon />,
        tags: ["react", "openai", "auth"],
        allowedLanguages: ["typescript", "javascript"],
        frontendFramework: "react-router",
      },
    ],
  },
  [AppTemplateType.BigCommerceApp]: {
    Base: [
      {
        default: true,
        title: "BigCommerce React app",
        description: "This template is a basic BigCommerce app that can be extended to create any BigCommerce app.",
        icon: (
          <TemplateIcon backgroundColor="#F8FFF0">
            <ReactIcon />
          </TemplateIcon>
        ),
        tags: ["react"],
        allowedLanguages: ["typescript", "javascript"],
      },
    ],
  },
  [AppTemplateType.WebAppWithAI]: {},
  [AppTemplateType.InternalTool]: {},
  [AppTemplateType.Empty]: {},
  [AppTemplateType.BackendApi]: {},
  [AppTemplateType.ChatGPTApp]: {
    Base: [
      {
        title: "React Router v7 framework mode",
        default: true,
        description: "Gadget's built-in email/password auth and Google Oauth powered by React Router v7 in framework mode",
        icon: ReactRouterTemplateIcon,
        frontendFramework: "react-router",
        tags: ["react-router"],
        allowedLanguages: ["typescript", "javascript"],
        withAutoComponents: "shadcn",
      },
      // TODO - Add this back when the authorize route is properly integrated into the router config
      // {
      //   default: false,
      //   title: "React Router v7 declarative mode",
      //   description: "This template has Gadget's built-in email/password auth and Google Oauth.",
      //   icon: ReactTemplateIcon,
      //   frontendFramework: "react",
      //   tags: ["react"],
      //   allowedLanguages: ["typescript", "javascript"],
      //   withAutoComponents: "shadcn",
      // },
    ],
  },
};

export type AppTemplateDescriptorWithType = AppTemplateDescriptor & { appTemplateType: AppTemplateType };

export const appTemplatesAsArray: AppTemplateDescriptorWithType[] = Object.entries(AppTemplates)
  .map(([appTemplateType, group]) =>
    Object.values(group)
      .flat()
      .map((descriptor) => ({
        appTemplateType: appTemplateType as AppTemplateType,
        ...descriptor,
      }))
  )
  .flat();

export default AppTemplates;
